import { useCallback, useMemo } from "react";
import { getLogger } from "@/Common/helpers/getLogger";
import { usePersistentSelection } from "@/Common/hooks/selection/usePersistentSelection";
import { useInventoryContext } from "./contexts/InventoryFilterContext";
import { PropertyPaths } from "@/Common/helpers/typeHelpers";

import { InventoryTab } from "./constants";
import { Checkbox, CheckboxProps } from "@mui/material";

import { useGridApiContext, useGridSelector } from "@mui/x-data-grid-pro";
import { GridStatePro } from "@mui/x-data-grid-pro/models/gridStatePro";
import get from "lodash-es/get";

const log = getLogger("SelectionAndFilterCheckboxAll");

export type OnChangeCallback<T> = (checked: boolean, rows: T[]) => void;

interface SelectionAndFilterCheckboxAllProps<T = unknown> extends CheckboxProps {
	data: T[];
	table: string;
	onChangeCallback?: OnChangeCallback<T>;
	pathToVinNumber: PropertyPaths<T, string>;
	origin: InventoryTab;
	commissionCycleId?: string;
	payPeriodId?: string;
}

export const SelectionAndFilterCheckboxAll = <T extends { _id: string }>(props: Readonly<SelectionAndFilterCheckboxAllProps<T>>) => {
	const { table, onChangeCallback, pathToVinNumber, origin, commissionCycleId, payPeriodId, data, disabled, ...checkboxProps } = props;

	const {
		addManyToFilter,
		removeManyFromFilter,
		commissionCycleId: contextCommissionCycleId,
		payPeriodId: contextPayPeriodId,
	} = useInventoryContext();

	const usedCommissionCycleId = commissionCycleId ?? contextCommissionCycleId;
	const usedPayPeriodId = payPeriodId ?? contextPayPeriodId;

	const { selectedIds } = usePersistentSelection({ table, commissionCycleId: usedCommissionCycleId, payPeriodId: usedPayPeriodId });

	const apiRef = useGridApiContext();

	const filteredRowIds = useGridSelector(apiRef, (state: GridStatePro) => {
		const lookup = state.filter.filteredRowsLookup;
		return Object.keys(lookup).filter((id) => lookup[id]);
	});

	const { checked, indeterminate } = useMemo(() => {
		const total = filteredRowIds.length;
		const totalSelected = filteredRowIds.filter((id) => selectedIds.includes(id)).length;
		return {
			indeterminate: Boolean(totalSelected) && totalSelected < total,
			checked: Boolean(totalSelected) && totalSelected === total,
		};
	}, [filteredRowIds, selectedIds]);

	const handleChangeCheckbox = useCallback<(_e: unknown, checked: boolean) => void>(
		(_e, isChecked) => {
			log("handleChangeCheckbox", isChecked);
			const payload = filteredRowIds.map((rowId) => {
				const checkedData = data.find((item) => item._id === rowId);
				const pathArray = pathToVinNumber.split(".");
				const vinNumber: string = get(checkedData, pathArray);
				return {
					table,
					origin,
					vinNumber,
					_id: rowId,
					commissionCycleId: usedCommissionCycleId,
					payPeriodId: usedPayPeriodId,
				};
			});
			isChecked && !indeterminate ? addManyToFilter(payload) : removeManyFromFilter(payload);
		},
		[table, onChangeCallback, usedCommissionCycleId, usedPayPeriodId, indeterminate, filteredRowIds]
	);

	const isActuallyDisabled = disabled || !filteredRowIds.length;

	return (
		<Checkbox
			{...checkboxProps}
			indeterminate={!isActuallyDisabled && indeterminate}
			checked={!isActuallyDisabled && checked}
			onChange={handleChangeCheckbox}
			disabled={isActuallyDisabled}
		/>
	);
};
